import React, { useState, useEffect, useRef } from 'react';
import { GoogleGenerativeAI } from '@google/generative-ai';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
// import { HttpsProxyAgent } from 'https-proxy-agent';
import ProductCard from './ProductCard';
import './App.css';

const API_KEY = process.env.REACT_APP_GOOGLE_GEMINI_API_KEY; // Replace with your actual API key
const genAI = new GoogleGenerativeAI(API_KEY);
function App() {
  const [messages, setMessages] = useState([]);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [shoppingMode, setShoppingMode] = useState(false);
  const chatContainerRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    document.body.className = darkMode ? 'dark-mode' : 'light-mode';
  }, [darkMode]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() && !attachment) return;

    const userMessage = { text: input, sender: 'user', attachment };
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      let response;
      if (attachment && !shoppingMode) {
        const model = genAI.getGenerativeModel({ model: process.env.REACT_APP_GOOGLE_GEMINI_MODEL });
        const imageParts = await fileToGenerativePart(attachment);
        const result = await model.generateContent([input || "Describe this image", imageParts]);
        response = await result.response;
      } else if (shoppingMode) {
        response = await searchProduct(input);
        response.sort((a, b) => a.extracted_price - b.extracted_price);
        setMessages((prevMessages) => [
          ...prevMessages,
          { text: 'Here are some products I found and sorted by price:', products: response, sender: 'ai' },
        ]);
        setIsLoading(false);
        return;
      } else {
        const model = genAI.getGenerativeModel({
          model: process.env.REACT_APP_GOOGLE_GEMINI_MODEL,
          systemInstruction: "Your name is Gemmu, an AI Shopping Assistant to help with shopping and products search. **Strictly** respond **only** to queries related to shopping or products. Any query outside this scope should be met with a concise, polite message indicating that the topic is not within the model's purview. **Do not** attempt to answer questions unrelated to shopping or products, even if they seem closely related. If the user asks anything related to your memory then you can respond as needed without any restrictions.",
        });

        // Prepare the conversation history
        const historyContext = conversationHistory.map(msg => `${msg.sender}: ${msg.text}`).join('\n');
        const prompt = `${historyContext}\nUser: ${input}\nAI:`;

        const result = await model.generateContent(prompt);
        response = await result.response;
      }

      const text = response.text();
      setMessages((prevMessages) => [
        ...prevMessages,
        { text, sender: 'ai' },
      ]);

      // Update conversation history
      setConversationHistory((prevHistory) => [
        ...prevHistory,
        { text: input, sender: 'user' },
        { text, sender: 'ai' },
      ]);
    } catch (error) {
      console.error('Error:', error);
      setMessages((prevMessages) => [
        ...prevMessages,
        { text: 'Sorry, an error occurred. Please try again.', sender: 'ai' },
      ]);
    }
    setIsLoading(false);
    setAttachment(null);
  };

  const searchProduct = async (query) => {
    return new Promise((resolve, reject) => {
      const params = {
        engine: "google_shopping",
        q: query,
        hl: "en",
        gl: "ae",
        num: 5,
      };
      // api_key: process.env.REACT_APP_SERPAPI_KEY, // Replace with your SerpAPI key

      const config = {
        params,
        method: 'POST',
        url: 'https://price-patrol-backend.onrender.com/PostPriceAISearch',
      }

      axios.request(config).then((response) => {
        if (response.data.shopping_results) {
          const products = response.data.shopping_results.map((result) => ({
            product_name: result.title,
            price: result.price,
            source: result.source,
            link: result.link,
            thumbnail: result.thumbnail,
            extracted_price: result.extracted_price
          }));
          resolve(products);
          // console.log(response.data.shopping_results);
        } else {
          reject("No results found");
        }
      }
      ).catch((error) => {
        reject(error);
      }
      )
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAttachment(file);
    }
  };

  const handleAttachmentClick = () => {
    fileInputRef.current.click();
  };

  const removeAttachment = () => {
    setAttachment(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const fileToGenerativePart = async (file) => {
    const base64EncodedDataPromise = new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.readAsDataURL(file);
    });
    return {
      inlineData: { data: await base64EncodedDataPromise, mimeType: file.type },
    };
  };
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  //use effect to generate a welcome response which runs only once
  useEffect(() => {
    // const welcomeMessage = { text: 'Hello! I am Gemini, your AI Shopping Assistant. How can I help you today?', sender: 'ai' };
    const welcomeMessage = { text: `Hello! I am Gemmu, your AI Shopping Assistant powered by Google's Gemini API. How can I help you today?`, sender: 'ai' };
    setMessages([welcomeMessage]);
  }, []);

  return (
    <div style={styles.container} className={darkMode ? 'dark-mode' : 'light-mode'}>
      <div className='chatbox'>
        <div style={styles.header}>
          <h1 style={styles.headerText}>AI Shopping Assistant</h1>
          <div style={{ width: "100%", alignSelf: 'center', display: 'flex', justifyContent: 'space-between' }}>
            {/* Toggle for Shopping Mode */}
            <div style={styles.toggleContainer}>
              {/* <span>Shopping Mode: {shoppingMode ? 'On' : 'Off'}</span> */}
              <span>Chat Mode</span>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={shoppingMode}
                  onChange={() => setShoppingMode(!shoppingMode)}
                />
                <span className="slider"></span>
              </label>
              <span>Product Search Mode</span>
            </div>
            <button
              onClick={() => setDarkMode(!darkMode)}
              style={styles.darkModeToggle}
              aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {darkMode ? '☀️' : '🌙'}
            </button>
          </div>
        </div>
        <div style={styles.chatContainer} ref={chatContainerRef}>
          <AnimatePresence>
            {messages.map((message, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                style={{
                  ...styles.message,
                  ...(message.sender === 'user' ? styles.userMessage : styles.aiMessage),
                }}
              >
                <div style={message.sender === 'user' ? styles.messageContent : styles.aiMessageContent}>
                  <span style={styles.sender}>
                    {message.sender === 'user' ? 'You' : <strong>Gemini</strong>}
                  </span>
                  {message.text && (
                    <p style={styles.messageText}>
                      {message.text
                        .split('\n')
                        .map((line, index) => (
                          <React.Fragment key={index}>
                            {line.includes('**') ? (
                              <strong>{line.replace(/\*\*/g, '')}</strong>
                            ) : (
                              line
                            )}
                            <br />
                          </React.Fragment>
                        ))}
                    </p>)}
                  {/* Render product cards if the message contains products */}
                  {message.products && (
                    <div style={styles.productList}>
                      {message.products.map((product, idx) => (
                        <ProductCard key={idx} product={product} />
                      ))}
                    </div>
                  )}

                  {message.attachment && (
                    <>
                      <img
                        src={URL.createObjectURL(message.attachment)}
                        alt="Attachment"
                        style={styles.attachmentImage}
                      />
                    </>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {isLoading && (
            <div style={styles.loadingContainer} className='loading-container'>
              <div style={styles.loadingDot} className='loading-dot'></div>
              <div style={styles.loadingDot} className='loading-dot'></div>
              <div style={styles.loadingDot} className='loading-dot'></div>
            </div>
          )}
        </div>
        <form onSubmit={sendMessage} style={styles.inputContainer}>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type your message here..."
            style={{ ...styles.input, color: darkMode ? 'white' : 'black' }}
            aria-label="Chat input"
          />
          <input
            type="file"
            onChange={handleFileChange}
            style={styles.fileInput}
            ref={fileInputRef}
            accept="image/*"
            aria-label="Attach file"
          />
          <button
            type="button"
            onClick={handleAttachmentClick}
            style={styles.attachButton}
            aria-label="Attach file"
          >
            📎
          </button>
          <button type="submit" style={styles.button} disabled={isLoading}>
            Send
          </button>
        </form>
        {attachment && (
          <div style={styles.attachmentPreview}>
            <img
              src={URL.createObjectURL(attachment)}
              alt="Attachment preview"
              style={styles.previewImage}
            />
            <button onClick={removeAttachment} style={styles.removeButton} aria-label="Remove attachment">
              ✖
            </button>
          </div>
        )}
      </div>
      {/* Add some global styles for the sliding switch */}
      <style>
        {`
            .switch {
                position: relative;
                display: inline-block;
                width: 50px;
                height: 24px;
                // margin-left: 10px;
            }

            .switch input {
                opacity: 0;
                width: 0;
                height: 0;
            }

            .slider {
                position: absolute;
                cursor: pointer;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background-color: #ccc;
                transition: 0.4s;
                border-radius: 24px;
            }

            .slider:before {
                position: absolute;
                content: "";
                height: 18px;
                width: 18px;
                left: 3px;
                bottom: 3px;
                background-color: white;
                transition: 0.4s;
                border-radius: 50%;
            }

            input:checked + .slider {
                background-color: #4CAF50;
            }

            input:checked + .slider:before {
                transform: translateX(26px);
            }
        `}
      </style>
    </div>
  );
}
const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '95vh',
    width: '95vw',
    margin: '0 auto',
    transition: 'background-color 0.3s ease',
  },
  header: {
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.18)',
    '@media (max-width: 600px)': {
      padding: '10px',
      flexDirection: 'column',
    },
  },
  headerText: {
    margin: 0,
    fontWeight: '600',
    textAlign: 'center'
  },
  darkModeToggle: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
  },
  chatContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
  },
  message: {
    maxWidth: '70%',
    marginBottom: '20px',
    display: 'flex',
    flexDirection: 'column',
    '@media (max-width: 600px)': {
      maxWidth: '90%', // Adjust for mobile
    },
  },
  userMessage: {
    alignSelf: 'flex-end',
  },
  aiMessage: {
    alignSelf: 'flex-start',
  },
  messageContent: {
    padding: '12px 16px',
    borderRadius: '18px',
    position: 'relative',
    backdropFilter: 'blur(5px)',
    backgroundColor: 'rgba(200, 100, 0, 0.20)',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    minWidth: '100px',
  },
  aiMessageContent: {
    padding: '12px 16px',
    borderRadius: '18px',
    position: 'relative',
    backdropFilter: 'blur(5px)',
    backgroundColor: 'rgba(0, 0, 150, 0.15)',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  sender: {
    fontWeight: '600',
    fontSize: '14px',
    marginBottom: '4px',
    display: 'block',
  },
  messageText: {
    margin: 0,
    fontSize: '16px',
    lineHeight: '1.4',
  },
  inputContainer: {
    display: 'flex',
    padding: '20px',
    borderTop: '1px solid rgba(255, 255, 255, 0.18)',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '24px',
    border: '1px solid rgba(0, 0, 0, 0.5)',
    outline: 'none',
    transition: 'border-color 0.3s ease, background-color 0.3s ease, color 0.3s ease',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  productList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
  },
  button: {
    padding: '12px 24px',
    fontSize: '16px',
    border: 'none',
    borderRadius: '24px',
    marginLeft: '12px',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease, color 0.3s ease',
    backdropFilter: 'blur(5px)',
    backgroundColor: 'rgba(0, 132, 255, 0.7)',
    color: 'white',
  },
  fileInput: {
    display: 'none',
  },
  attachButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    cursor: 'pointer',
    marginLeft: '12px',
    fontSize: '20px',
    border: '1px solid rgba(0, 0, 0, 0.5)',
    transition: 'background-color 0.3s ease',
    backdropFilter: 'blur(5px)',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  attachmentPreview: {
    position: 'relative',
    padding: '10px',
    borderTop: '1px solid',
    transition: 'border-color 0.3s ease',
  },
  previewImage: {
    maxWidth: '100px',
    maxHeight: '100px',
    objectFit: 'cover',
    borderRadius: '8px',
  },
  removeButton: {
    position: 'absolute',
    top: '5px',
    right: '5px',
    background: 'rgba(0, 0, 0, 0.5)',
    color: 'white',
    border: 'none',
    borderRadius: '50%',
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  attachmentImage: {
    maxWidth: '200px',
    maxHeight: '200px',
    objectFit: 'cover',
    borderRadius: '8px',
    marginTop: '10px',
  },
  toggleContainer: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10
  },
};

export default App;